/**
 * Tour Planner routing geometry — driving via Mapbox Directions, flights as
 * great-circle arcs, trains as a straight visual. Shared with the calendar Map tab.
 */
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';

export const FLIGHT_DISTANCE_THRESHOLD_MILES = 300;

export function straightLineDistanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Great-circle arc points between two lat/lng (for flight paths). Returns [lng, lat][]. */
export function greatCircleArcPoints(lat1, lng1, lat2, lng2, numPoints = 32) {
  const toRad = (d) => d * (Math.PI / 180);
  const toDeg = (r) => r * (180 / Math.PI);
  const lat1R = toRad(lat1);
  const lng1R = toRad(lng1);
  const lat2R = toRad(lat2);
  const lng2R = toRad(lng2);
  const x1 = Math.cos(lat1R) * Math.cos(lng1R);
  const y1 = Math.cos(lat1R) * Math.sin(lng1R);
  const z1 = Math.sin(lat1R);
  const x2 = Math.cos(lat2R) * Math.cos(lng2R);
  const y2 = Math.cos(lat2R) * Math.sin(lng2R);
  const z2 = Math.sin(lat2R);
  const dot = Math.max(-1, Math.min(1, x1 * x2 + y1 * y2 + z1 * z2));
  const omega = Math.acos(dot);
  const coords = [];
  const sinOmega = Math.sin(omega);
  if (sinOmega < 1e-10) {
    for (let i = 0; i <= numPoints; i += 1) {
      const t = i / numPoints;
      coords.push([lng1 + t * (lng2 - lng1), lat1 + t * (lat2 - lat1)]);
    }
    return coords;
  }
  for (let i = 0; i <= numPoints; i += 1) {
    const t = i / numPoints;
    const s1 = Math.sin((1 - t) * omega) / sinOmega;
    const s2 = Math.sin(t * omega) / sinOmega;
    const x = s1 * x1 + s2 * x2;
    const y = s1 * y1 + s2 * y2;
    const z = s1 * z1 + s2 * z2;
    const lat = toDeg(Math.asin(Math.max(-1, Math.min(1, z))));
    const lng = toDeg(Math.atan2(y, x));
    coords.push([lng, lat]);
  }
  return coords;
}

function asLngLat(point) {
  if (Array.isArray(point)) return [Number(point[0]), Number(point[1])];
  return [Number(point.lng), Number(point.lat)];
}

export function getFlightLegGeometry(startCoords, endCoords) {
  const startLng = startCoords[0];
  const startLat = startCoords[1];
  const endLng = endCoords[0];
  const endLat = endCoords[1];
  const arcCoords = greatCircleArcPoints(startLat, startLng, endLat, endLng, 32);
  const distKm = straightLineDistanceKm(startLat, startLng, endLat, endLng);
  const distMiles = distKm * 0.621371;
  const flightMinutes = 30 + (distMiles / 500) * 60;
  return {
    requiresFlight: true,
    distance: distKm,
    duration: flightMinutes,
    geometry: { type: 'LineString', coordinates: arcCoords },
  };
}

export function getTrainLegGeometry(startCoords, endCoords) {
  const start = [startCoords[0], startCoords[1]];
  const end = [endCoords[0], endCoords[1]];
  const distKm = straightLineDistanceKm(start[1], start[0], end[1], end[0]);
  return {
    isTrain: true,
    distance: distKm,
    duration: distKm * 0.15,
    geometry: { type: 'LineString', coordinates: [start, end] },
  };
}

export function fallbackStraightGeometry(startCoords, endCoords) {
  const startLat = startCoords[1];
  const startLng = startCoords[0];
  const endLat = endCoords[1];
  const endLng = endCoords[0];
  const straightKm = straightLineDistanceKm(startLat, startLng, endLat, endLng);
  return {
    distance: straightKm,
    duration: (straightKm / 50) * 60,
    geometry: { type: 'LineString', coordinates: [startCoords, endCoords] },
    fallbackStraightLine: true,
  };
}

/**
 * Driving directions (Mapbox) or an automatic flight arc when the hop is long
 * and forceDrive is not set. Same contract as Tour Planner's getDirections.
 */
export async function getDirections(start, end, options = {}) {
  const startCoords = asLngLat(start);
  const endCoords = asLngLat(end);
  const startLat = startCoords[1];
  const startLng = startCoords[0];
  const endLat = endCoords[1];
  const endLng = endCoords[0];
  const distKm = straightLineDistanceKm(startLat, startLng, endLat, endLng);
  const distMiles = distKm * 0.621371;

  if (!options.forceDrive && distMiles >= FLIGHT_DISTANCE_THRESHOLD_MILES) {
    return getFlightLegGeometry(startCoords, endCoords);
  }

  const token = options.accessToken || MAPBOX_ACCESS_TOKEN;
  const fetchFn = options.fetch || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
  if (!token || !fetchFn) {
    return fallbackStraightGeometry(startCoords, endCoords);
  }

  try {
    const coordsStr = `${startCoords[0]},${startCoords[1]};${endCoords[0]},${endCoords[1]}`;
    const radiusOptions = [50, 200, 500];
    for (const radius of radiusOptions) {
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}?geometries=geojson&overview=full&radiuses=${radius};${radius}&access_token=${token}`;
      const response = await fetchFn(url);
      if (!response || !response.ok) continue;
      const data = await response.json();
      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        return {
          distance: route.distance / 1000,
          duration: route.duration / 60,
          geometry: route.geometry,
        };
      }
    }
    return fallbackStraightGeometry(startCoords, endCoords);
  } catch {
    return fallbackStraightGeometry(startCoords, endCoords);
  }
}

export function normalizePlannerLegMode(raw) {
  const m = String(raw || 'drive').toLowerCase();
  if (m.includes('flight') || m.includes('fly') || m === 'air') return 'flight';
  if (m.includes('train') || m.includes('rail')) return 'train';
  return 'drive';
}

/** Consecutive calendar-map stops → planner legs (drive / flight / train). */
export function routingLegsFromStops(stops) {
  const list = Array.isArray(stops) ? stops : [];
  const legs = [];
  for (let i = 1; i < list.length; i += 1) {
    const from = list[i - 1];
    const to = list[i];
    legs.push({
      from,
      to,
      mode: normalizePlannerLegMode(to.mode),
      key: `hop-${from.dateKey || i - 1}-${to.dateKey || i}`,
    });
  }
  return legs;
}

const geometryCache = new Map();

export function routingCacheKey(startCoords, endCoords, mode) {
  return `${Number(startCoords[0]).toFixed(5)},${Number(startCoords[1]).toFixed(5)}|${Number(endCoords[0]).toFixed(5)},${Number(endCoords[1]).toFixed(5)}|${mode}`;
}

export async function fetchLegGeometry(startCoords, endCoords, mode, options = {}) {
  const normalized = normalizePlannerLegMode(mode);
  const key = routingCacheKey(startCoords, endCoords, normalized);
  if (!options.skipCache && geometryCache.has(key)) return geometryCache.get(key);

  let route;
  if (normalized === 'train') {
    route = getTrainLegGeometry(startCoords, endCoords);
  } else if (normalized === 'flight') {
    route = getFlightLegGeometry(startCoords, endCoords);
  } else {
    route = await getDirections(startCoords, endCoords, { forceDrive: true, ...options });
  }
  if (!options.skipCache && route?.geometry && !route.fallbackStraightLine) {
    geometryCache.set(key, route);
  }
  return route;
}

export function kmToMiles(km) {
  return (Number(km) || 0) * 0.621371;
}
